import React, { useState, Fragment } from "react";
import { __ } from "@wordpress/i18n";
import { Button } from "@wpmudev/react-button";
import apiFetch from "@wordpress/api-fetch";
import { Notifications } from "@wpmudev/react-notifications";
import { StarAltIcon, CheckIcon } from "../../icons";

const PluginCard = ({
  as = "div",
  logo,
  title,
  rating,
  active_installs,
  description,
  features,
  url,
  active: isActiveProp,
  installed: isInstalledProp,
  slug,
  cta = false,
  path,
  ...delegated
}) => {
  const [isInstalled, setIsInstalled] = useState(isInstalledProp);
  const [isActive, setIsActive] = useState(isActiveProp);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);

  const TagName = as;

  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000); // Hide after 5 seconds
  };

  const handleInstallPlugin = async () => {
    setLoading(true);
    try {
      const response = await apiFetch({
        path: window.wpmudev_cross_sell_data.restEndpointInstall,
        method: "POST",
        data: {
          plugin_slug: slug,
          current_slug: window.wpmudev_cross_sell_data.current_slug,
        },
      });

      if (response.success) {
        setIsInstalled(true);
        showNotification(
          <Fragment>
            <b>{__(title, "plugin-cross-sell-textdomain")}</b>
            {__(
              " plugin has been installed successfully!",
              "plugin-cross-sell-textdomain"
            )}
          </Fragment>
        );
      } else {
        console.error("Installation failed:", response.message);
        showNotification(
          __(
            `Installation failed: ${response.message}`,
            "plugin-cross-sell-textdomain"
          ),
          "error"
        );
      }
    } catch (error) {
      console.error("Error installing plugin:", error);
      showNotification(
        __(
          `Error installing plugin: ${error.message}`,
          "plugin-cross-sell-textdomain"
        ),
        "error"
      );
    }
    setLoading(false);
  };

  const handleActivatePlugin = async () => {
    setLoading(true);
    try {
      const response = await apiFetch({
        path:
          window.wpmudev_cross_sell_data.restEndpointActivate ||
          "/wpmudev/v1/activate-plugin",
        method: "POST",
        data: {
          plugin_slug: slug,
          current_slug: window.wpmudev_cross_sell_data.current_slug,
        },
      });

      if (response.success) {
        setIsActive(true);
        showNotification(
          <Fragment>
            <b>{__(title, "plugin-cross-sell-textdomain")}</b>
            {__(
              " plugin has been activated successfully!",
              "plugin-cross-sell-textdomain"
            )}
          </Fragment>
        );
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        console.error("Activation failed:", response.message);
        showNotification(
          __(
            `Activation failed: ${response.message}`,
            "plugin-cross-sell-textdomain"
          ),
          "error"
        );
      }
    } catch (error) {
      console.error("Error activating plugin:", error);
      showNotification(
        __(
          `Error activating plugin: ${error.message}`,
          "plugin-cross-sell-textdomain"
        ),
        "error"
      );
    }
    setLoading(false);
  };

  return (
    <Fragment>
      {notification && (
        <div className="sui-floating-notices">
          <Notifications type={notification.type}>
            <p>{notification.message}</p>
          </Notifications>
        </div>
      )}
      <TagName
        className="cross-sell-card"
        {...(TagName === "a" ? { href: url, target: "_blank" } : {})}
        {...delegated}
      >
        <div className="cross-sell-plugin-card-header">
          <div>
            <img src={logo} alt={title || "Plugin logo"} />
          </div>
          {cta && (
            <>
              {!isInstalled ? (
                <Button
                  label={
                    loading
                      ? __("Installing...", "plugin-cross-sell-textdomain")
                      : __("Install", "plugin-cross-sell-textdomain")
                  }
                  design="ghost"
                  onClick={handleInstallPlugin}
                  disabled={loading}
                />
              ) : !isActive ? (
                <Button
                  label={
                    loading
                      ? __("Activating...", "plugin-cross-sell-textdomain")
                      : __("Activate", "plugin-cross-sell-textdomain")
                  }
                  color="blue"
                  onClick={handleActivatePlugin}
                  disabled={loading}
                />
              ) : (
                <Button
                  label={__("Active", "plugin-cross-sell-textdomain")}
                  design="ghost"
                  color="green"
                  style={{ pointerEvents: "none" }}
                />
              )}
            </>
          )}
        </div>
        <div className="cross-sell-plugin-card-content">
          <h2>{__(title, "plugin-cross-sell-textdomain")}</h2>
          <div className="cross-sell-plugin-card-rating">
            <div className="cross-sell-plugin-card-rating__stars">
              {__(rating, "plugin-cross-sell-textdomain")}
              <StarAltIcon />
            </div>
            <div className="cross-sell-plugin-card-rating__installs">
              {__(
                `${active_installs} active installs`,
                "plugin-cross-sell-textdomain"
              )}
            </div>
          </div>
          <span className="cross-sell-plugin-card-description">
            {__(description, "plugin-cross-sell-textdomain")}
          </span>
          {features && features.length > 0 && (
            <div className="cross-sell-plugin-card-features">
              {features.map((feature, index) => (
                <div key={index} className="cross-sell-plugin-card-feature">
                  <CheckIcon />
                  {__(feature, "plugin-cross-sell-textdomain")}
                </div>
              ))}
            </div>
          )}
        </div>
      </TagName>
    </Fragment>
  );
};

export { PluginCard };
